import { useContext } from 'react'
import { WppButton } from '@wppopen/components-library-react'
import ViewTaskPageContext from 'context/viewTaskPageContext/viewTaskPageContext'
import MenuListPopUp from 'components/MenuListPopUp/MenuListPopUp'
import clsx from 'clsx'
import { DEFAULT_TAB } from 'pages/ViewTask/utils'
import ButtonSkeleton from 'components/Skeletons/ButtonSkeleton'
import usePermissions from 'hooks/usePermissions'
import type { pdpTabIds } from 'components/Tabs/tabs-type.d'
import type { JobsDetailData } from '@contracts'
import styles from './RenderNavButtons.module.scss'

interface RenderNavButtonsProps {
  status: JobsDetailData['status']
  onDownloadClick?: () => void
  isDownloading?: boolean
  onApproveClick?: () => void
  isCurrentlyEditing?: boolean
  activeTab?: pdpTabIds
  isEditNeeded?: boolean
  className?: string
  isInitialising?: boolean
  isLoading?: boolean
  clientId?: string
}

export default function RenderNavButtons({
  status,
  onDownloadClick,
  isDownloading = false,
  onApproveClick,
  isCurrentlyEditing,
  activeTab = DEFAULT_TAB,
  isEditNeeded,
  className,
  isInitialising,
  isLoading,
  clientId,
}: RenderNavButtonsProps) {
  const { setIsPageEditable, isPageEditable, contentTypeBeingEdited, setIsResubmitModalOpen } =
    useContext(ViewTaskPageContext)
  const isAiActiveTab = activeTab !== DEFAULT_TAB
  const disabledExtraActions =
    ['blocked', 'executing', 'processing'].includes(status || '') ||
    isAiActiveTab ||
    !!contentTypeBeingEdited ||
    isInitialising ||
    isLoading

  const handleRedirect = () => {
    setIsResubmitModalOpen(true)
  }

  const { clientLevelPermissions } = usePermissions({
    clientId,
    permissionSubset: [
      'PDP_APP_POST_JOBS_BY_APPROVE',
      'PDP_APP_POST_JOBS_BY_RESUBMIT',
      'PDP_APP_GET_JOBS_BY_EXPORT',
      'PDP_APP_PATCH_JOBS_BY_CONTENT_TYPES_BY',
    ],
  })

  const [canApproveJob, canResubmitJob, canExportJob, canPatchJob] = clientLevelPermissions

  return (
    <div className={clsx(styles.actionButtonContainer, className)} data-testid="actions-section">
      {/* Render the resubmit button if task failed */}
      {canResubmitJob && (
        <>
          {status === 'failed' ? (
            <WppButton
              onClick={handleRedirect}
              variant="primary"
              data-testid="resubmit-button"
              disabled={isAiActiveTab}
            >
              Resubmit
            </WppButton>
          ) : (
            <MenuListPopUp
              isDisabled={disabledExtraActions}
              menuItems={[
                {
                  label: 'Resubmit',
                  onClick: handleRedirect,
                },
              ]}
            />
          )}
        </>
      )}

      {isInitialising && <ButtonSkeleton />}

      {/* Render download and edit buttons if status is completed */}
      {['completed'].includes(status || '') && (
        <>
          {canPatchJob && isEditNeeded && (
            <WppButton
              onClick={() => setIsPageEditable(!isPageEditable)}
              variant="secondary"
              disabled={isCurrentlyEditing || isAiActiveTab}
              data-testid="edit-button"
            >
              {isPageEditable ? 'Cancel edit' : 'Edit'}
            </WppButton>
          )}
          {canExportJob && (
            <WppButton
              onClick={onDownloadClick}
              variant="primary"
              loading={isDownloading}
              disabled={!onDownloadClick || isPageEditable || isAiActiveTab || isLoading}
              data-testid="download-button"
            >
              Download
            </WppButton>
          )}
        </>
      )}

      {/* Render approve disable button if status is not failed and not completed */}
      {canApproveJob && status && !['failed', 'completed'].includes(status) && (
        <WppButton
          onClick={onApproveClick}
          variant="primary"
          disabled={status !== 'ready_for_review' || isCurrentlyEditing}
          data-testid="approve-button"
        >
          Approve
        </WppButton>
      )}
    </div>
  )
}
